import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { formatDate } from '../utils/helpers';
import api from '../utils/api';
import { toast } from 'react-toastify';

// When Wallet Plug is enabled (the live processor), joining redirects to its
// hosted checkout, billed as a subscription for the plan period. The card-capture
// form below is the no-processor fallback (admin charges the stored card).
const WALLETPLUG_ENABLED = String(process.env.REACT_APP_WALLETPLUG_ENABLED).toLowerCase() === 'true';

const INPUT =
  'w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 ' +
  'placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-tea-400 focus:border-transparent transition';
const LABEL = 'block text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1.5';

const BENEFITS = [
  { t: 'VIP pricing on everything', d: 'Unlock the lowest member price on every peptide in the catalog — automatically applied at checkout.' },
  { t: 'Free shipping, always', d: 'Every order ships free for the life of your membership. No minimums, no codes.' },
  { t: 'Locked-in savings', d: 'Your VIP rate is protected even as retail prices rise.' },
  { t: 'VIP-only drops', d: 'Early access to new compounds and member-only offers.' },
];

export default function MembershipPage() {
  const { user, refreshUser } = useAuthCtx();
  const { fmt } = useCurrency();
  const navigate = useNavigate();

  const [plans, setPlans]           = useState(null);
  const [membership, setMembership] = useState(null);
  const [loaded, setLoaded]         = useState(false);
  const [plan, setPlan]             = useState('annual');
  const [stage, setStage]           = useState('choose'); // choose → pay → done
  const [membershipId, setMembershipId] = useState(null);
  const [working, setWorking]       = useState(false);

  // Card + billing capture
  const defaultName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const [cardNumber,   setCardNumber]   = useState('');
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear,  setCardExpYear]  = useState('');
  const [cardCvc,      setCardCvc]      = useState('');
  const [billingName,       setBillingName]       = useState(defaultName);
  const [billingLine1,      setBillingLine1]      = useState('');
  const [billingLine2,      setBillingLine2]      = useState('');
  const [billingCity,       setBillingCity]       = useState('');
  const [billingProvince,   setBillingProvince]   = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingCountry,    setBillingCountry]    = useState('US');
  const [agreed,            setAgreed]            = useState(false);

  useEffect(() => {
    api.get('/memberships')
      .then(res => { setPlans(res.data.plans); setMembership(res.data.membership); })
      .catch(() => toast.error('Failed to load membership info'))
      .finally(() => setLoaded(true));
  }, []);

  const priceCents = plans ? plans[plan].priceInCents : (plan === 'monthly' ? 2000 : 6000);
  const cadence = plan === 'monthly' ? 'month' : 'year';

  const rawCard = cardNumber.replace(/\s+/g, '');
  const cardComplete =
    /^\d{12,19}$/.test(rawCard) &&
    Number(cardExpMonth) >= 1 && Number(cardExpMonth) <= 12 &&
    /^\d{4}$/.test(cardExpYear) &&
    /^\d{3,4}$/.test(cardCvc);
  const addressComplete =
    billingName.trim() && billingLine1.trim() && billingCity.trim() &&
    billingProvince.trim() && billingPostalCode.trim() && billingCountry.trim().length === 2;
  const canPay = cardComplete && addressComplete && agreed && !working;

  const handleCardNumberChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
    setCardNumber(digits.replace(/(.{4})/g, '$1 ').trim());
  };

  // Step 1 — create the pending membership. With Wallet Plug live, hand off to
  // its hosted checkout; otherwise reveal the card-capture fallback form.
  const startJoin = async () => {
    setWorking(true);
    try {
      const res = await api.post('/memberships/join', { plan });
      const id = res.data.id;
      setMembershipId(id);

      if (WALLETPLUG_ENABLED) {
        const { data } = await api.post(`/memberships/${id}/walletplug/initiate`);
        if (data.alreadyActive) {
          setMembership(data.membership);
          await refreshUser();
          toast.info('You already have an active VIP membership.');
          return;
        }
        window.location.href = data.paymentUrl;   // leave the SPA for hosted checkout
        return;
      }

      setStage('pay');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      // Already a member → reflect it.
      if (err.response?.status === 409 && err.response.data?.membership) {
        setMembership(err.response.data.membership);
        toast.info('You already have an active VIP membership.');
      } else {
        toast.error(err.response?.data?.error || 'Could not start membership');
      }
    } finally {
      setWorking(false);
    }
  };

  // Step 2 — capture card + activate.
  const submitPayment = async (e) => {
    e.preventDefault();
    if (!canPay) return;
    setWorking(true);
    try {
      const res = await api.post(`/memberships/${membershipId}/confirm-card`, {
        cardNumber: rawCard,
        cardExpMonth: Number(cardExpMonth),
        cardExpYear: Number(cardExpYear),
        cardCvc: cardCvc.trim(),
        billingName: billingName.trim(),
        billingLine1: billingLine1.trim(),
        billingLine2: billingLine2.trim() || null,
        billingCity: billingCity.trim(),
        billingProvince: billingProvince.trim(),
        billingPostalCode: billingPostalCode.trim(),
        billingCountry: billingCountry.trim().toUpperCase(),
      });
      setMembership(res.data);
      setStage('done');
      await refreshUser();      // VIP pricing lights up site-wide
      toast.success('Welcome to VIP — your member pricing is now unlocked.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Payment failed');
    } finally {
      setWorking(false);
    }
  };

  const alreadyVip = loaded && membership && membership.active;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-tea-50 via-white to-white">
      <Navbar />

      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

        {/* ── Already a VIP ───────────────────────────────────────── */}
        {alreadyVip ? (
          <ActiveMemberCard membership={membership} fmt={fmt} onShop={() => navigate('/shop')} />
        ) : stage === 'done' ? (
          <SuccessCard membership={membership} fmt={fmt} onShop={() => navigate('/shop')} />
        ) : (
          <>
            {/* Hero */}
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-tea-600 font-bold uppercase tracking-[0.25em] text-[11px] mb-3">
                Aloha Amino Membership
              </p>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Become a <span className="text-tea-600">VIP</span> & unlock member pricing
              </h1>
              <p className="mt-4 text-sm text-gray-500 leading-relaxed">
                One membership unlocks VIP prices on every product plus free shipping on every order —
                for a full {cadence}. Cancel anytime.
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Left: plan choice OR payment */}
              <div className="md:col-span-3">
                {stage === 'choose' ? (
                  <div className="rounded-2xl bg-white shadow-md p-6 sm:p-7">
                    <h2 className="font-bold text-gray-900 text-lg mb-4">Choose your plan</h2>
                    <div className="space-y-3">
                      <PlanOption
                        active={plan === 'annual'} onClick={() => setPlan('annual')}
                        title="Annual VIP" price="$60" cadence="/year"
                        badge="Best value · save 75%"
                        sub="That's just $5/mo — billed once a year."
                      />
                      <PlanOption
                        active={plan === 'monthly'} onClick={() => setPlan('monthly')}
                        title="Monthly VIP" price="$20" cadence="/month"
                        sub="Flexible — billed every month."
                      />
                    </div>

                    <button
                      onClick={startJoin}
                      disabled={working}
                      className="mt-6 w-full rounded-full bg-tea-600 hover:bg-tea-700 text-white text-sm font-bold py-3 tracking-wide transition-colors shadow-sm disabled:opacity-60"
                    >
                      {working ? 'One moment…' : `Continue · ${fmt(priceCents)}/${cadence}`}
                    </button>
                    <p className="mt-3 text-center text-[11px] text-gray-400">
                      Secure checkout · Cancel anytime from your account
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submitPayment} className="rounded-2xl bg-white shadow-md p-6 sm:p-7 space-y-5">
                    <div className="flex items-center justify-between">
                      <h2 className="font-bold text-gray-900 text-lg">Payment details</h2>
                      <button type="button" onClick={() => setStage('choose')}
                        className="text-xs font-semibold text-tea-600 hover:text-tea-700">← Change plan</button>
                    </div>

                    {/* Billing details */}
                    <div className="space-y-3">
                      <div>
                        <label className={LABEL}>Name on card</label>
                        <input className={INPUT} value={billingName} onChange={e => setBillingName(e.target.value)}
                          placeholder="Full legal name" maxLength={120} autoComplete="cc-name" required />
                      </div>
                      <div>
                        <label className={LABEL}>Street address</label>
                        <input className={INPUT} value={billingLine1} onChange={e => setBillingLine1(e.target.value)}
                          placeholder="123 Main St" maxLength={255} autoComplete="billing address-line1" required />
                      </div>
                      <div>
                        <label className={LABEL}>Apt / suite (optional)</label>
                        <input className={INPUT} value={billingLine2} onChange={e => setBillingLine2(e.target.value)}
                          placeholder="Apt 4B" maxLength={255} autoComplete="billing address-line2" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={LABEL}>City</label>
                          <input className={INPUT} value={billingCity} onChange={e => setBillingCity(e.target.value)}
                            placeholder="City" maxLength={120} required />
                        </div>
                        <div>
                          <label className={LABEL}>State / province</label>
                          <input className={INPUT} value={billingProvince} onChange={e => setBillingProvince(e.target.value)}
                            placeholder="CA" maxLength={80} required />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={LABEL}>ZIP / postal</label>
                          <input className={INPUT} value={billingPostalCode} onChange={e => setBillingPostalCode(e.target.value)}
                            placeholder="90210" maxLength={20} required />
                        </div>
                        <div>
                          <label className={LABEL}>Country</label>
                          <input className={INPUT} value={billingCountry}
                            onChange={e => setBillingCountry(e.target.value.toUpperCase().slice(0, 2))}
                            placeholder="US" maxLength={2} style={{ textTransform: 'uppercase' }} required />
                        </div>
                      </div>
                    </div>

                    {/* Card */}
                    <div className="pt-2 border-t border-gray-100 space-y-3">
                      <div>
                        <label className={LABEL}>Card number</label>
                        <input className={INPUT} value={cardNumber} onChange={handleCardNumberChange}
                          placeholder="1234 5678 9012 3456" inputMode="numeric" autoComplete="cc-number"
                          style={{ letterSpacing: '0.08em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} required />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className={LABEL}>Exp month</label>
                          <input className={INPUT} value={cardExpMonth} onChange={e => setCardExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                            placeholder="MM" inputMode="numeric" maxLength={2} autoComplete="cc-exp-month" required />
                        </div>
                        <div>
                          <label className={LABEL}>Exp year</label>
                          <input className={INPUT} value={cardExpYear} onChange={e => setCardExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="YYYY" inputMode="numeric" maxLength={4} autoComplete="cc-exp-year" required />
                        </div>
                        <div>
                          <label className={LABEL}>CVC</label>
                          <input className={INPUT} value={cardCvc} onChange={e => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="123" inputMode="numeric" maxLength={4} autoComplete="cc-csc" required />
                        </div>
                      </div>
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer select-none">
                      <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded accent-tea-600 shrink-0" />
                      <span className="text-xs leading-relaxed text-gray-500">
                        I authorize Aloha Amino to charge{' '}
                        <strong className="text-gray-800">{fmt(priceCents)} USD</strong>{' '}
                        every {cadence}, renewing automatically until I cancel.
                      </span>
                    </label>

                    <button type="submit" disabled={!canPay}
                      className="w-full rounded-full bg-tea-600 hover:bg-tea-700 text-white text-sm font-bold py-3 tracking-wide transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                      {working ? 'Activating…' : `Pay ${fmt(priceCents)} & unlock VIP`}
                    </button>
                  </form>
                )}
              </div>

              {/* Right: benefits */}
              <aside className="md:col-span-2">
                <div className="rounded-2xl bg-tea-600 text-white p-6 sm:p-7 shadow-md">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-tea-100 mb-4">
                    What you get
                  </p>
                  <ul className="space-y-4">
                    {BENEFITS.map(b => (
                      <li key={b.t} className="flex gap-3">
                        <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">✓</span>
                        <div>
                          <div className="font-bold text-sm">{b.t}</div>
                          <div className="text-tea-100 text-xs leading-relaxed mt-0.5">{b.d}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function PlanOption({ active, onClick, title, price, cadence, badge, sub }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all ${
        active ? 'border-tea-600 bg-tea-50 shadow-sm' : 'border-gray-200 bg-white hover:border-tea-300'
      }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? 'border-tea-600' : 'border-gray-300'}`}>
            {active && <span className="w-2 h-2 rounded-full bg-tea-600" />}
          </span>
          <span className="font-bold text-gray-900">{title}</span>
        </div>
        <div className="text-right">
          <span className="text-xl font-extrabold text-gray-900">{price}</span>
          <span className="text-xs text-gray-400 font-medium">{cadence}</span>
        </div>
      </div>
      {badge && (
        <span className="inline-block mt-2 ml-6 text-[10px] font-bold uppercase tracking-wide text-tea-700 bg-tea-100 rounded-full px-2 py-0.5">
          {badge}
        </span>
      )}
      {sub && <p className="mt-1.5 ml-6 text-xs text-gray-500">{sub}</p>}
    </button>
  );
}

function ActiveMemberCard({ membership, fmt, onShop }) {
  return (
    <div className="max-w-xl mx-auto text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-tea-100 text-tea-600 text-3xl mb-5">★</div>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">You're a VIP</h1>
      <p className="mt-3 text-sm text-gray-500">
        Your member pricing and free shipping are active on every order.
      </p>
      <div className="mt-7 rounded-2xl bg-white shadow-md p-6 text-left">
        <Row label="Plan" value={membership.plan === 'monthly' ? 'Monthly VIP' : 'Annual VIP'} />
        <Row label="Fee" value={`${fmt(membership.priceInCents)} / ${membership.plan === 'monthly' ? 'month' : 'year'}`} />
        <Row label={membership.autoRenew ? 'Renews' : 'Access until'}
          value={membership.currentPeriodEnd ? formatDate(membership.currentPeriodEnd) : '—'} />
        {membership.cardLast4 && <Row label="Card" value={`•••• ${membership.cardLast4}`} />}
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={onShop} className="rounded-full bg-tea-600 hover:bg-tea-700 text-white text-sm font-bold px-8 py-3 transition-colors shadow-sm">
          Shop with VIP pricing →
        </button>
        <Link to="/account" className="rounded-full border border-gray-200 text-gray-600 hover:border-tea-400 hover:text-tea-600 text-sm font-bold px-8 py-3 transition-colors">
          Manage membership
        </Link>
      </div>
    </div>
  );
}

function SuccessCard({ membership, fmt, onShop }) {
  return <ActiveMemberCard membership={membership} fmt={fmt} onShop={onShop} />;
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{value}</span>
    </div>
  );
}
